import React from 'react';
import { motion } from 'framer-motion';
import { whyAIData } from '../mockData';
import { Zap, Clock, Target, Users, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const WhyAI = () => {
  useSEO({
    title: 'Why AI-Powered Web Design | Hei Web Creations Singapore',
    description:
      'See how we combine AI workflows with human design expertise to deliver premium websites 50% faster and more affordably — without compromising quality.',
  });

  const icons = [Zap, Clock, Target, Users, TrendingUp];

  return (
    <div className="pt-32 bg-white" data-testid="why-ai-page">
      {/* Hero Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#1A1A1A] mb-6 tracking-tight">
              {whyAIData.title}
            </h1>
            <p className="text-xl md:text-2xl text-gray-500">{whyAIData.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-6">
          <div className="space-y-24">
            {whyAIData.benefits.map((benefit, index) => {
              const Icon = icons[index];
              const isEven = index % 2 === 0;
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true, margin: '-100px' }}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
                    isEven ? '' : 'lg:grid-flow-dense'
                  }`}
                >
                  {/* Icon & Number */}
                  <div className={isEven ? '' : 'lg:col-start-2'}>
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                      viewport={{ once: true }}
                      className="relative"
                    >
                      <div className="w-24 h-24 rounded-3xl bg-[#FAFAFA] flex items-center justify-center mb-6">
                        <Icon className="text-[#C8102E]" size={48} />
                      </div>
                      <span className="absolute -top-8 -left-8 text-9xl font-bold text-[#C8102E]/10">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </motion.div>
                  </div>

                  {/* Content */}
                  <div className={isEven ? '' : 'lg:col-start-1 lg:row-start-1'}>
                    <motion.h3
                      initial={{ opacity: 0, x: isEven ? -30 : 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.3 }}
                      viewport={{ once: true }}
                      className="text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-6"
                    >
                      {benefit.title}
                    </motion.h3>
                    <motion.p
                      initial={{ opacity: 0, x: isEven ? -30 : 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.4 }}
                      viewport={{ once: true }}
                      className="text-lg md:text-xl text-gray-600 leading-relaxed"
                    >
                      {benefit.description}
                    </motion.p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-32 bg-[#FAFAFA]">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1A1A1A] mb-6 tracking-tight">
              The Difference
            </h2>
            <p className="text-lg md:text-xl text-gray-500 max-w-3xl mx-auto">
              See how AI-enhanced workflows benefit you
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                label: 'Turnaround Time',
                traditional: '6-8 weeks',
                enhanced: '1-3 weeks',
                improvement: '50-88% faster',
              },
              {
                label: 'Cost',
                traditional: '$3,000-$8,000',
                enhanced: '$888-$2,500',
                improvement: '50-80% savings',
              },
              {
                label: 'Quality',
                traditional: 'Premium',
                enhanced: 'Premium',
                improvement: 'Same quality',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
              >
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-6 text-center">
                  {item.label}
                </h3>
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-1">Traditional</p>
                    <p className="text-2xl font-bold text-gray-700">{item.traditional}</p>
                  </div>
                  <div className="text-center py-2 bg-[#C8102E]/10 rounded-lg">
                    <p className="text-sm font-semibold text-[#C8102E]">{item.improvement}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500 mb-1">AI-Enhanced</p>
                    <p className="text-2xl font-bold text-[#C8102E]">{item.enhanced}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto bg-[#1A1A1A] rounded-[2.5rem] px-8 py-20 md:px-16"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              Experience the future of web development
            </h2>
            <p className="text-lg md:text-xl text-gray-400 mb-12 leading-relaxed max-w-2xl mx-auto">
              Let's create something exceptional with the perfect blend of human creativity and AI efficiency.
            </p>
            <Link to="/contact">
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="inline-block">
                <Button
                  className="bg-[#C8102E] hover:bg-white hover:text-[#1A1A1A] text-white px-10 py-6 text-base rounded-full transition-all duration-300"
                  data-testid="why-ai-cta-button"
                >
                  Start Your Project
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </motion.div>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WhyAI;
